/**
 * /p/$slug — صفحة عامّة ديناميكيّة من منشئ الصفحات.
 *
 * تجلب الصفحة وبلوكاتها من DB (RLS: المنشورة فقط)، وتبني head() ديناميكياً
 * مع canonical + OG + JSON-LD WebPage.
 * SiteShell يلتقط الـpath تلقائياً ويعرض breadcrumbs (إن لم نُخفِها).
 */
import * as React from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteShell } from "@/components/SiteShell";
import { BlocksList } from "@/components/customBlocks/BlockRenderer";
import { getPublicPageBySlug } from "@/utils/customPages.functions";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
import { useLocale } from "@/contexts/LocaleContext";

import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/p/$slug")({
  headers: publicCacheHeaders,
  loader: async ({ params }) => {
    const { page, blocks } = await getPublicPageBySlug({ data: { slug: params.slug } });
    if (!page) throw notFound();
    return { page, blocks };
  },
  head: ({ params, loaderData }) => {
    const page = loaderData?.page;
    const title = page?.title_en || page?.title_ar || params.slug;
    const description = page?.seo_description_en || page?.seo_description_ar || "";
    const url = `${PUBLIC_SITE_ORIGIN}/p/${params.slug}`;
    const meta: Array<
      | { title: string }
      | { name: string; content: string }
      | { property: string; content: string }
    > = [
      { title: `${title} — Lecmo` },
      { property: "og:title", content: title },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
    ];
    if (description) {
      meta.push({ name: "description", content: description });
      meta.push({ property: "og:description", content: description });
    }
    if (page?.og_image_url) {
      meta.push({ property: "og:image", content: page.og_image_url });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: title,
            description: description || undefined,
            url,
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto w-full max-w-2xl px-4 py-24 text-center sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">الصفحة غير موجودة</h1>
        <p className="mt-2 text-muted-foreground">Page not found.</p>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => (
    <SiteShell>
      <div className="mx-auto w-full max-w-2xl px-4 py-24 text-center sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">حدث خطأ</h1>
        <p className="mt-2 text-muted-foreground">{(error as Error)?.message ?? "Error"}</p>
        <button
          onClick={reset}
          className="mt-4 rounded-md border border-border px-4 py-2 min-h-[44px]"
        >
          إعادة المحاولة
        </button>
      </div>
    </SiteShell>
  ),
  component: PublicCustomPage,
});

function PublicCustomPage() {
  const { page, blocks } = Route.useLoaderData();
  const { locale } = useLocale();
  const heading = locale === "ar" ? page.title_ar || page.title_en : page.title_en || page.title_ar;
  // إن لم يكن أوّل بلوك Hero — أضف عنواناً افتراضياً
  const firstIsHero = blocks[0]?.block_type === "hero";
  return (
    <SiteShell>
      {!firstIsHero && heading ? (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-8 md:pt-12">
          <h1 className="text-3xl md:text-4xl font-semibold text-foreground">{heading}</h1>
        </div>
      ) : null}
      <BlocksList blocks={blocks} />
    </SiteShell>
  );
}

